import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import FormDialog from "@/components/FormDialog";
import { Plus, Search, Pencil, Archive, ArchiveRestore, Trash2, Inbox, CheckCircle2, XCircle } from "lucide-react";
import { inhaftColumns, inhaftFields, inhaftDefault } from "@/lib/entities";

export default function HaftantrittView({ mode = "aktiv", dn = "", testidPrefix = "haftantritt" }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(inhaftDefault(dn)());
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type, row}

  const isArchive = mode !== "aktiv";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/haftantritt", { params: { status: mode } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshEffect(load);

  const openCreate = () => {
    setEditing(null);
    setValues(inhaftDefault(dn)());
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setValues({ ...row });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = inhaftFields.reduce((acc, f) => ({ ...acc, [f.name]: values[f.name] ?? "" }), {});
      if (editing) {
        await api.put(`/haftantritt/${editing.id}`, payload);
        toast.success("Haftantritt aktualisiert");
      } else {
        await api.post("/haftantritt", payload);
        toast.success("Haftantritt eingetragen");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const archive = async (row, status) => {
    try {
      await api.patch(`/haftantritt/${row.id}/archive`, null, { params: { status } });
      toast.success(status === "erschienen" ? "Als erschienen archiviert" : "Als nicht erschienen archiviert");
      load();
      if (status === "nicht_erschienen") {
        navigate("/parole", { state: { createParoleName: row.taetername, createParoleHaftantrittId: row.id } });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    }
  };

  const doConfirm = async () => {
    if (!confirm) return;
    const { type, row } = confirm;
    try {
      if (type === "delete") {
        await api.delete(`/haftantritt/${row.id}`);
        toast.success("Eintrag gelöscht");
      } else if (type === "restore") {
        await api.patch(`/haftantritt/${row.id}/restore`);
        toast.success("Wiederhergestellt");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const filtered = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return inhaftColumns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid={`${testidPrefix}-search`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen…"
            className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary"
          />
        </div>
        {!isArchive && (
          <Button
            data-testid={`${testidPrefix}-create-btn`}
            onClick={openCreate}
            className="ml-auto rounded-sm bg-primary hover:bg-blue-600 text-white gap-2"
          >
            <Plus className="h-4 w-4" /> Haftantritt eintragen
          </Button>
        )}
      </div>

      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {inhaftColumns.map((c) => (
                <TableHead key={c.key} className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11">
                  {c.label}
                </TableHead>
              ))}
              <TableHead className="text-right text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 w-[140px]">
                Aktionen
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={inhaftColumns.length + 1} className="text-center py-10 text-zinc-500 font-mono text-sm">
                  Wird geladen…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={inhaftColumns.length + 1} className="text-center py-14">
                  <div className="flex flex-col items-center gap-2 text-zinc-600">
                    <Inbox className="h-8 w-8" />
                    <span className="font-mono text-sm">Keine Einträge vorhanden</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id} data-testid={`${testidPrefix}-row`} className="border-zinc-800 hover:bg-zinc-900/50 transition-colors duration-150">
                  {inhaftColumns.map((c) => (
                    <TableCell key={c.key} className={c.mono ? "font-mono text-sm text-zinc-300 align-top" : "text-sm text-zinc-300 align-top"}>
                      {c.render ? c.render(row[c.key], row) : row[c.key] || <span className="text-zinc-600">—</span>}
                    </TableCell>
                  ))}
                  <TableCell className="text-right align-top">
                    <div className="flex items-center justify-end gap-1">
                      {!isArchive && (
                        <>
                          <Button data-testid={`${testidPrefix}-edit`} size="icon" variant="ghost" onClick={() => openEdit(row)} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-primary hover:bg-primary/10" title="Bearbeiten">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button data-testid={`${testidPrefix}-archive`} size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10" title="Archivieren">
                                <Archive className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
                              <DropdownMenuItem data-testid={`${testidPrefix}-archive-erschienen`} onClick={() => archive(row, "erschienen")} className="rounded-sm text-emerald-400 focus:text-emerald-300 focus:bg-emerald-500/10 gap-2">
                                <CheckCircle2 className="h-4 w-4" /> Erschienen
                              </DropdownMenuItem>
                              <DropdownMenuItem data-testid={`${testidPrefix}-archive-nicht-erschienen`} onClick={() => archive(row, "nicht_erschienen")} className="rounded-sm text-red-400 focus:text-red-300 focus:bg-red-500/10 gap-2">
                                <XCircle className="h-4 w-4" /> Nicht Erschienen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                      {isArchive && (
                        <Button data-testid={`${testidPrefix}-restore`} size="icon" variant="ghost" onClick={() => setConfirm({ type: "restore", row })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10" title="Wiederherstellen">
                          <ArchiveRestore className="h-4 w-4" />
                        </Button>
                      )}
                      <Button data-testid={`${testidPrefix}-delete`} size="icon" variant="ghost" onClick={() => setConfirm({ type: "delete", row })} className="h-8 w-8 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10" title="Löschen">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Haftantritt bearbeiten" : "Haftantritt eintragen"}
        fields={inhaftFields}
        values={values}
        setValues={setValues}
        onSubmit={submit}
        submitting={submitting}
        testidPrefix={testidPrefix}
      />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">
              {confirm?.type === "delete" ? "Eintrag löschen?" : "Eintrag wiederherstellen?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirm?.type === "delete"
                ? "Dieser Vorgang kann nicht rückgängig gemacht werden."
                : "Der Eintrag wird zurück in den aktiven Bereich verschoben."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testidPrefix}-confirm`} onClick={doConfirm} className={`rounded-sm ${confirm?.type === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-blue-600"} text-white`}>
              Bestätigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
